/**
 * Agent service account management: create, list, update, delete.
 * Agent session management: create sessions, attach to running sessions.
 *
 * Agents are bot accounts that can launch sessions via API keys,
 * designed for CI/CD pipelines and automated workflows.
 *
 * Usage:
 *   spacecode agent create --name "claude-ci"
 *   spacecode agent list
 *   spacecode agent update <id> --webhook https://example.com/hook
 *   spacecode agent delete <id>
 *   spacecode agent session "fix the tests" -R owner/repo
 *   spacecode agent session attach <id>
 *   SPACECODE_TOKEN=sct_abc123 spacecode -p "fix tests" -R owner/repo
 */

import type { Command } from 'commander';
import chalk from 'chalk';
import { execFileSync } from 'node:child_process';
import { requireAuth } from './auth.js';
import { loadAuth, loadConfig } from '../config.js';
import { SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import { AgentClient } from '../agent/client.js';
import { FileSync } from '../agent/file-sync.js';

/** Format a timestamp as a human-readable relative age. */
function formatAge(timestampMs: number): string {
  const diff = Date.now() - timestampMs;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Resolve the sandbox worker base URL (HTTPS).
 * Prefers env override, then config, then the shared constant.
 */
export async function getTtyUrl(): Promise<string> {
  const config = await loadConfig();
  return process.env.SANDBOX_WORKER_URL ?? config.apiUrl?.replace('backend.', 'tty.') ?? SANDBOX_WORKER_URL;
}

/**
 * Get the auth token for sandbox-worker requests.
 * Uses requireAuth() to ensure the user is logged in, then extracts the token.
 */
export async function getAuthToken(): Promise<string> {
  const client = await requireAuth();
  const token = client.getToken();
  if (!token) {
    // Fallback to reading auth file directly
    const auth = await loadAuth();
    if (!auth.accessToken) {
      throw new Error('Not authenticated. Run `spacecode login` first.');
    }
    return auth.accessToken;
  }
  return token;
}

/**
 * Try to detect the git repo root from the current working directory.
 * Returns null if not inside a git repo.
 */
export function findGitRoot(): string | null {
  try {
    return execFileSync('git', ['rev-parse', '--show-toplevel'], { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }).trim();
  } catch {
    return null;
  }
}

/**
 * Create an agent session via the sandbox worker API.
 */
export interface CreateSessionResult {
  sessionId: string;
  /** Extracted work-item summary from a prior turn, present only when resuming a session that has one (EPIC-2 TICKET-2.2). */
  summary?: string | null;
}

export async function createSession(
  ttyUrl: string,
  token: string,
  repoName?: string,
  sessionId?: string,
  name?: string,
): Promise<CreateSessionResult> {
  const res = await fetch(`${ttyUrl}/api/agent/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ repoName, sessionId, name }),
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Failed to create agent session (${res.status}): ${body}`);
  }

  const data = (await res.json()) as { sessionId: string; resumed?: boolean; summary?: string | null };
  return { sessionId: data.sessionId, summary: data.summary ?? null };
}

/**
 * List agent sessions for the current user.
 */
export async function listSessions(
  ttyUrl: string,
  token: string,
  limit?: number,
): Promise<Array<{ id: string; repo_id: string | null; status: string; name: string | null; created_at: number; updated_at: number; message_count: number; last_prompt: string | null }>> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const res = await fetch(`${ttyUrl}/api/agent/sessions?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Failed to list sessions (${res.status})`);
  }
  const data = (await res.json()) as { sessions: any[] };
  return data.sessions;
}

export interface WorkItem {
  id: string;
  repo_id: string;
  session_id: string;
  kind: 'decision' | 'work_item';
  description: string;
  status: 'open' | 'done';
  created_at: string;
  updated_at: string;
  session_name: string | null;
}

/**
 * List extracted work items (and decisions) across the current user's sessions.
 * Defaults to open work items; pass status: 'all' to include done/decisions too.
 */
export async function listWorkItems(
  ttyUrl: string,
  token: string,
  options: { repo?: string; status?: 'open' | 'done' | 'all'; limit?: number } = {},
): Promise<WorkItem[]> {
  const params = new URLSearchParams();
  if (options.repo) params.set('repo', options.repo);
  if (options.status) params.set('status', options.status);
  if (options.limit) params.set('limit', String(options.limit));
  const res = await fetch(`${ttyUrl}/api/agent/work-items?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Failed to list work items (${res.status}): ${body}`);
  }
  const data = (await res.json()) as { workItems: WorkItem[] };
  return data.workItems;
}

/**
 * Parse a work item's `created_at`/`updated_at` (D1 TEXT, SQLite's bare
 * "YYYY-MM-DD HH:MM:SS" UTC format with no offset) into epoch ms.
 * `new Date(...)` alone would parse that string as local time.
 */
export function parseWorkItemTimestamp(sqliteUtc: string): number {
  return new Date(sqliteUtc.replace(' ', 'T') + 'Z').getTime();
}

/**
 * Look up a session by name. Returns the session ID or null if not found.
 */
export async function lookupSessionByName(
  ttyUrl: string,
  token: string,
  name: string,
): Promise<string | null> {
  const res = await fetch(`${ttyUrl}/api/agent/sessions?name=${encodeURIComponent(name)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { session?: { id: string } };
  return data.session?.id ?? null;
}

/**
 * Build the WebSocket URL for a session.
 */
export function buildWsUrl(ttyUrl: string, sessionId: string, token: string): string {
  const wsBase = ttyUrl.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
  return `${wsBase}/api/agent/session/${sessionId}/ws?token=${encodeURIComponent(token)}`;
}

/**
 * Connect to an agent session, send a prompt (if provided), render events,
 * and optionally sync files.
 */
async function runAgentSession(opts: {
  sessionId: string;
  ttyUrl: string;
  token: string;
  prompt?: string;
}): Promise<void> {
  const { sessionId, ttyUrl, token, prompt } = opts;
  const wsUrl = buildWsUrl(ttyUrl, sessionId, token);

  console.log(chalk.dim(`  Session: ${sessionId}`));
  console.log(chalk.dim(`  WebSocket: ${wsUrl.replace(/token=[^&]+/, 'token=***')}`));
  console.log('');

  // Set up file sync if we're in a git repo
  const gitRoot = findGitRoot();
  let fileSync: FileSync | null = null;

  const agentClient = new AgentClient({
    wsUrl,
    onEvent: (event) => {
      // Forward file_changed events to FileSync
      fileSync?.handleServerEvent(event);
    },
    onDone: (success) => {
      fileSync?.stop();
      process.exit(success ? 0 : 1);
    },
    onError: (message) => {
      console.error(chalk.red(`\nAgent error: ${message}`));
      fileSync?.stop();
      process.exit(1);
    },
  });

  // Handle SIGINT — cancel the agent gracefully
  let sigintCount = 0;
  const sigintHandler = () => {
    sigintCount++;
    if (sigintCount === 1) {
      console.log(chalk.dim('\nCancelling agent... (press Ctrl+C again to force quit)'));
      agentClient.cancel();
    } else {
      console.log(chalk.dim('\nForce quitting...'));
      fileSync?.stop();
      agentClient.close();
      process.exit(130);
    }
  };
  process.on('SIGINT', sigintHandler);

  try {
    await agentClient.connect();
    console.log(chalk.green('Connected to agent session.'));

    // Set up file sync after connection
    if (gitRoot) {
      fileSync = new FileSync({
        repoRoot: gitRoot,
        send: (msg) => {
          agentClient.send(msg);
        },
      });
      fileSync.start();
      console.log(chalk.dim(`  File sync: ${gitRoot}`));
    }

    // Send the initial prompt if provided
    if (prompt) {
      console.log(chalk.dim(`  Prompt: ${prompt}\n`));
      agentClient.prompt(prompt);
    }

    // Keep the process alive until done/error callbacks fire
    await new Promise(() => {
      // Resolved by onDone/onError calling process.exit()
    });
  } catch (error) {
    console.error(chalk.red(`Failed to connect: ${error instanceof Error ? error.message : String(error)}`));
    fileSync?.stop();
    process.exit(1);
  } finally {
    process.off('SIGINT', sigintHandler);
  }
}

export function registerAgentCommand(program: Command): void {
  const agent = program
    .command('agent')
    .description('Manage AI agent service accounts and sessions');

  agent
    .command('create')
    .description('Create a new agent service account')
    .requiredOption('--name <name>', 'Agent name (lowercase, hyphens, e.g. "claude-ci")')
    .option('--description <desc>', 'Purpose description')
    .option('--webhook <url>', 'Webhook URL for completion notifications')
    .action(async (options: { name: string; description?: string; webhook?: string }) => {
      try {
        const client = await requireAuth();
        const result = await client.createAgent(options.name, {
          description: options.description,
          webhookUrl: options.webhook,
        });

        console.log(chalk.green('\nAgent created successfully!\n'));
        console.log(`  ${chalk.bold('Name:')}     ${result.agent.profile.displayName}`);
        console.log(`  ${chalk.bold('ID:')}       ${result.agent.id}`);
        console.log(`  ${chalk.bold('Login:')}    ${result.agent.githubLogin}`);
        console.log(`  ${chalk.bold('API Key:')}  ${result.apiKey.token}`);
        if (result.agent.profile.webhookUrl) {
          console.log(`  ${chalk.bold('Webhook:')}  ${result.agent.profile.webhookUrl}`);
        }
        console.log();
        console.log(chalk.yellow('Save the API key — it will not be shown again.'));
        console.log(chalk.gray('\nUsage:'));
        console.log(chalk.gray(`  SPACECODE_TOKEN=${result.apiKey.token} spacecode -p "your prompt" -R owner/repo`));
      } catch (error) {
        console.error(chalk.red(`Failed to create agent: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  agent
    .command('list')
    .description('List your agent service accounts')
    .action(async () => {
      try {
        const client = await requireAuth();
        const { agents } = await client.listAgents();

        if (agents.length === 0) {
          console.log(chalk.gray('No agents. Create one with: spacecode agent create --name <name>'));
          return;
        }

        console.log(chalk.bold(`\n  Agents (${agents.length}):\n`));

        for (const a of agents) {
          const created = new Date(a.createdAt).toLocaleDateString();
          console.log(`  ${chalk.cyan(a.profile.displayName)} ${chalk.gray(`(${a.id})`)}`);
          console.log(`    Login: ${a.githubLogin}  Created: ${created}`);
          if (a.profile.description) console.log(`    ${chalk.gray(a.profile.description)}`);
          if (a.profile.webhookUrl) console.log(`    Webhook: ${a.profile.webhookUrl}`);
          console.log();
        }
      } catch (error) {
        console.error(chalk.red(`Failed to list agents: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  agent
    .command('update <id>')
    .description('Update an agent service account')
    .option('--description <desc>', 'Update description')
    .option('--webhook <url>', 'Update webhook URL')
    .option('--max-sandboxes <n>', 'Max concurrent sandboxes', parseInt)
    .action(async (id: string, options: { description?: string; webhook?: string; maxSandboxes?: number }) => {
      try {
        if (options.maxSandboxes !== undefined && (isNaN(options.maxSandboxes) || options.maxSandboxes < 1)) {
          console.error(chalk.red('--max-sandboxes must be a positive number'));
          process.exit(1);
        }
        const client = await requireAuth();
        const { agent: updated } = await client.updateAgent(id, {
          description: options.description,
          webhookUrl: options.webhook,
          maxConcurrentSandboxes: options.maxSandboxes,
        });
        console.log(chalk.green(`Agent ${updated.profile.displayName} updated.`));
      } catch (error) {
        console.error(chalk.red(`Failed to update agent: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  agent
    .command('delete <id>')
    .description('Delete an agent and revoke all its API keys')
    .action(async (id: string) => {
      try {
        const client = await requireAuth();
        await client.deleteAgent(id);
        console.log(chalk.green(`Agent ${id} deleted. All API keys revoked.`));
      } catch (error) {
        console.error(chalk.red(`Failed to delete agent: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  agent
    .command('sessions')
    .description('List recent agent sessions')
    .option('-a, --all', 'Show all sessions (default: 20 most recent)')
    .option('--limit <n>', 'Number of sessions to show', '20')
    .action(async (options: { all?: boolean; limit: string }) => {
      try {
        const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);
        const limit = options.all ? 100 : parseInt(options.limit, 10);
        const sessions = await listSessions(ttyUrl, token, limit);

        if (sessions.length === 0) {
          console.log(chalk.gray('No sessions found. Start one with: spacecode owner/repo'));
          return;
        }

        console.log(chalk.bold(`\n  Agent Sessions (${sessions.length}):\n`));

        for (const s of sessions) {
          const age = formatAge(s.updated_at);
          const statusColor = s.status === 'active' ? chalk.green : s.status === 'idle' ? chalk.yellow : chalk.gray;
          const label = s.name ? chalk.cyan(s.name) : chalk.gray(s.id.slice(0, 8));
          const repo = s.repo_id ? chalk.dim(` ${s.repo_id}`) : '';
          const msgCount = s.message_count ? chalk.dim(` ${s.message_count} msgs`) : '';
          console.log(`  ${label}  ${statusColor(s.status)}${repo}${msgCount}  ${chalk.dim(age)}`);
          if (s.name) console.log(`    ${chalk.dim(`ID: ${s.id}`)}`);
          if (s.last_prompt) {
            // Parse JSON-encoded content and truncate
            let preview = '';
            try {
              const parsed = JSON.parse(s.last_prompt);
              preview = typeof parsed === 'string' ? parsed : String(parsed);
            } catch {
              preview = String(s.last_prompt);
            }
            if (preview.length > 60) preview = preview.slice(0, 57) + '...';
            console.log(`    ${chalk.dim(`> ${preview}`)}`);
          }
        }

        console.log('');
        console.log(chalk.dim('  Resume with: spacecode --session <id>'));
        console.log('');
      } catch (error) {
        console.error(chalk.red(`Failed: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  // ── Agent Session subcommands ──────────────────────────────────────

  const session = agent
    .command('session <prompt>')
    .description('Create a new agent session, connect via WebSocket, and send a prompt')
    .option('-R, --repo <repo>', 'GitHub repo to work on (owner/repo)')
    .action(async (prompt: string, options: { repo?: string }) => {
      try {
        const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);

        console.log('');
        console.log(chalk.bold('Creating agent session...'));
        if (options.repo) {
          console.log(chalk.dim(`  Repo: ${options.repo}`));
        }

        const { sessionId } = await createSession(ttyUrl, token, options.repo);

        await runAgentSession({ sessionId, ttyUrl, token, prompt });
      } catch (error) {
        console.error(chalk.red(`Failed: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  session
    .command('attach <id>')
    .description('Attach to an existing agent session via WebSocket')
    .action(async (id: string) => {
      try {
        const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);

        console.log('');
        console.log(chalk.bold(`Attaching to agent session ${chalk.cyan(id)}...`));

        await runAgentSession({ sessionId: id, ttyUrl, token });
      } catch (error) {
        console.error(chalk.red(`Failed: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });
}
